import { IWarehouse, IWarehouseGroup } from '../lookups';
import { IUnallocatedProduct } from '../unallocatedProducts';
import { LogisticsActionKeys as keys } from './logisticsActionKeys';
import * as i from './logisticsInterfaces';

export const logisticsActionCreators = {
  addItemsToContainer: (shipmentContainerId: number, unallocatedProducts: IUnallocatedProduct[]) =>
    ({ payload: { shipmentContainerId, unallocatedProducts }, type: keys.ADD_ITEMS_TO_CONTAINER } as const),
  addItemsToLoad: (loadNumber: string, unallocatedProducts: IUnallocatedProduct[]) =>
    ({
      payload: { loadNumber, unallocatedProducts },
      type: keys.ADD_ITEMS_TO_LOAD
    } as const),
  addShipmentContainer: (shipmentId: number, shipmentContainerTypeId: number) =>
    ({ payload: { shipmentId, shipmentContainerTypeId }, type: keys.ADD_SHIPMENT_CONTAINER } as const),
  advanceState: (loadNumber: string) => ({ payload: { loadNumber }, type: keys.ADVANCE_LOAD_STATE } as const),
  createLoad: (payload: i.ICreateLoadRequest) => ({ payload, type: keys.CREATE_LOAD } as const),
  deleteContainer: (shipmentContainerId: number) =>
    ({ payload: { shipmentContainerId }, type: keys.DELETE_SHIPMENT_CONTAINER } as const),
  deleteItem: (payload: { shipmentItemId: number; shipmentId: number }) =>
    ({ payload, type: keys.DELETE_SHIPMENT_ITEM } as const),
  deleteLoad: (loadNumber: string) => ({ payload: { loadNumber }, type: keys.DELETE_LOAD }),
  deleteShipment: (shipmentId: number) => ({ payload: { shipmentId }, type: keys.DELETE_SHIPMENT }),
  deleteShipmentKit: (shipmentId: number, kitItemId: string) =>
    ({ payload: { shipmentId, kitItemId }, type: keys.DELETE_SHIPMENT_KIT } as const),
  deleteWarehouseGroup: (payload: { warehouseGroupId: number }) =>
    ({ payload, type: keys.DELETE_WAREHOUSE_GROUP } as const),
  fetchCalendar: (payload: { start: Date; end: Date }) =>
    ({
      payload,
      type: keys.FETCH_LOAD_CALENDAR
    } as const),
  fetchCarrier: (carrierId: string) => ({ payload: { carrierId }, type: keys.FETCH_CARRIER } as const),
  fetchCarrierLoads: (carrierId: string) => ({ payload: { carrierId }, type: keys.FETCH_CARRIER_LOADS } as const),
  fetchCarriers: () => ({ type: keys.FETCH_CARRIERS } as const),
  fetchDailySummaries: (payload: { startDate: Date; endDate: Date }) =>
    ({ payload, type: keys.FETCH_LOGISTICS_DAILY_SUMMARIES } as const),
  fetchEvents: (loadNumber: string) => ({ payload: { loadNumber }, type: keys.FETCH_LOAD_EVENTS } as const),
  fetchLoad: (loadNumber: string) =>
    ({
      payload: { loadNumber },
      type: keys.FETCH_LOAD
    } as const),
  fetchRoute: (loadNumber: string) => ({
    payload: { loadNumber },
    type: keys.FETCH_LOAD_ROUTE
  }),
  fetchShipment: (shipmentId: number) => ({ payload: shipmentId, type: keys.FETCH_SHIPMENT } as const),
  fetchShipmentList: () =>
    ({
      type: keys.FETCH_SHIPMENT_LIST
    } as const),
  fetchShipmentListFulfilled: (shipments: i.IShipmentListItem[]) =>
    ({
      payload: shipments,
      type: keys.FETCH_SHIPMENT_LIST_FULFILLED
    } as const),
  fetchShipmentListRejected: () =>
    ({
      error: true,
      payload: '',
      type: keys.FETCH_SHIPMENT_LIST_REJECTED
    } as const),
  fetchShipmentRejected: () =>
    ({
      error: true,
      payload: '',
      type: keys.FETCH_SHIPMENT_REJECTED
    } as const),
  moveShipmentItems: (payload: i.IMoveShipmentRequest) => ({ payload, type: keys.MOVE_SHIPMENT_ITEMS } as const),
  removeContainer: (shipmentContainerId: number) =>
    ({
      payload: { shipmentContainerId },
      type: keys.REMOVE_SHIPMENT_CONTAINER
    } as const),
  removeItems: (shipmentItemIds: number[]) =>
    ({ payload: { shipmentItemIds }, type: keys.REMOVE_SHIPMENT_ITEMS } as const),
  removeLoad: (loadNumber: string) => ({ payload: { loadNumber }, type: keys.REMOVE_LOAD }),
  removeRoute: (loadNumber: string) => ({ payload: { loadNumber }, type: keys.REMOVE_LOAD_ROUTE }),
  removeShipment: (payload: { loadNumber: string; shipmentIds: number[] }) => ({ payload, type: keys.REMOVE_SHIPMENT }),
  setCarriers: (payload: i.ICarrier[]) => ({ payload, type: keys.SET_CARRIERS } as const),
  setContainer: (payload: i.IShipmentContainerBase | i.IShipmentContainerWithItems) =>
    ({ payload, type: keys.SET_SHIPMENT_CONTAINER } as const),
  setDailySummaries: (payload: i.ILogisticsDailySummaryApiResponse[]) =>
    ({ payload, type: keys.SET_LOGISTICS_DAILY_SUMMARIES } as const),
  setEvents: (payload: i.ILoadEvent[]) => ({ payload, type: keys.SET_LOAD_EVENTS } as const),
  setFilters: (payload: { type: 'dashboard' | 'calendar'; filters: i.ILogisticsFilters }) =>
    ({ payload, type: keys.SET_LOGISTICS_FILTERS } as const),
  setFinalDestination: (payload: { loadNumber: string; shipmentId: number | null }) =>
    ({ payload, type: keys.SET_LOAD_FINAL_DESTINATION } as const),
  setLoad: (payload: i.ILoadApiResponse) =>
    ({
      payload,
      type: keys.SET_LOAD
    } as const),
  setLoadPartial: (payload: Pick<i.ILoadApiResponse, 'loadNumber'> & Partial<i.ILoadApiResponse>) =>
    ({ payload, type: keys.SET_LOAD_PARTIAL } as const),
  setLoadValue: (payload: { loadNumber: string; value: number }) => ({ payload, type: keys.SET_LOAD_VALUE } as const),
  setMinLoads: (payload: i.ILoadMin[]) =>
    ({
      payload,
      type: keys.SET_MIN_LOADS
    } as const),
  setRoute: (payload: i.ILoadRoute) => ({ payload, type: keys.SET_LOAD_ROUTE }),
  setShipment: (shipment: i.IShipmentApiResponse) =>
    ({
      payload: shipment,
      type: keys.SET_SHIPMENT
    } as const),
  setShipmentItems: (payload: i.IShipmentItem[]) => ({ payload, type: keys.SET_SHIPMENT_ITEMS } as const),
  setShipmentLoadState: (payload: { shipmentId: number; loadStateId: number }) =>
    ({ payload, type: keys.SET_SHIPMENT_LOADSTATE } as const),
  setShipmentStopNumbers: (payload: i.IShipmentStopNumber[]) =>
    ({ payload, type: keys.SET_SHIPMENT_STOP_NUMBERS } as const),
  splitItem: (payload: i.ISplitShipmentItemRequest) => ({ payload, type: keys.SPLIT_SHIPMENT_ITEM } as const),
  toggleShipmentListLoading: (dateString: string) =>
    ({
      payload: dateString,
      type: keys.TOGGLE_SHIPMENT_LIST_LOADING
    } as const),
  updateContainer: (payload: i.IShipmentContainer) => ({ payload, type: keys.UPDATE_SHIPMENT_CONTAINER } as const),
  updateDepartureDate: (payload: i.IChangeLoadDateRequest) =>
    ({ payload, type: keys.UPDATE_LOAD_DEPARTURE_DATE } as const),
  updateFees: (loadNumber: string, loadFees: i.ILoadFee[]) =>
    ({
      payload: { loadNumber, loadFees },
      type: keys.UPDATE_LOAD_FEES
    } as const),
  updateLoad: (payload: { loadNumber: string; update: Partial<i.ILoad> }) =>
    ({ payload, type: keys.UPDATE_LOAD } as const),
  updateRoute: (payload: i.ILoadRouteUpdateRequest) => ({ payload, type: keys.UPDATE_LOAD_ROUTE } as const),
  updateShipment: (payload: i.UpdateShipmentRequest) => ({ payload, type: keys.UPDATE_SHIPMENT } as const),
  updateWarehouse: (payload: IWarehouse) => ({ payload, type: keys.UPDATE_WAREHOUSE } as const),
  updateWarehouseGroup: (payload: IWarehouseGroup) => ({ payload, type: keys.UPDATE_WAREHOUSE_GROUP } as const)
};
